import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ROLE } from '../../constants/common';
import { createOrGetMatrixRoom, getMatrixRoomInfo } from '../../actions/matrixAction';
import { getUserRole } from '../function';
import { computeRoomIsPriority } from './matrixReadUtils';

/**
 * Fetches and exposes Matrix room info for a given destination userId.
 */
export const useMatrixRoomInfo = (userId) => {
  const dispatch = useDispatch();
  const creatingRoomRef = useRef(false);

  const refresh = useCallback(
    async ({ force = true, background = false } = {}) => {
      if (!userId) {
        return { success: false, error: 'User ID is required' };
      }

      const result = await dispatch(getMatrixRoomInfo(userId, { force, background }));
      return result;
    },
    [dispatch, userId]
  );

  const entry = useSelector((state) =>
    userId ? state.matrix?.roomInfoByUserId?.[userId] ?? null : null
  );

  const data = entry?.data ?? null;

  // Only block UI on the first load; background refreshes keep showing cached room info.
  const loading = !!entry?.loading && !data;
  const error = entry?.error ?? null;

  const roomId = data?.room_id ?? null;
  const destUserInfo = data?.destUser ?? null;
  const destUserId = destUserInfo?._id;
  const isSubscribed = Boolean(destUserInfo?.subscribed);
  const isCreatorReplyToTip = Boolean(destUserInfo?.isCreatorReplyToTip);
  const hasPriority = computeRoomIsPriority(destUserInfo);
  const isAlreadyMessageTip = Boolean(data?.isAlreadyMessageTip);
  const messagePurchaseContentIds = data?.messagePurchaseContentIds ?? [];

  useEffect(() => {
    if (!userId) return;
    // Refetch on page entry. When room info is already cached (e.g. chat is open and
    // IncomingCallModal mounts the same userId), refresh in the background so the
    // message list does not flash "Loading chat...".
    dispatch(getMatrixRoomInfo(userId, { force: true, background: Boolean(data) }));
  }, [dispatch, userId]);

  useEffect(() => {
    const role = getUserRole();
    const isCreator = role === ROLE.CREATOR_SUB || role === ROLE.CREATOR_CONTENT;
    const shouldEnsureRoom = isSubscribed || isCreator;

    if (!shouldEnsureRoom || !destUserId || roomId || creatingRoomRef.current) return;

    creatingRoomRef.current = true;

    (async () => {
      try {
        // createOrGetMatrixRoom is a plain async API helper — not a Redux thunk; do not dispatch its Promise.
        const response = await createOrGetMatrixRoom(destUserId);

        if (response?.success) {
          dispatch(getMatrixRoomInfo(userId, { force: true, background: Boolean(data) }));
        }
      } finally {
        creatingRoomRef.current = false;
      }
    })();
  }, [isSubscribed, destUserId, roomId, userId, dispatch]);

  return {
    loading,
    roomId,
    hasPriority,
    isSubscribed,
    isCreatorReplyToTip,
    isAlreadyMessageTip,
    messagePurchaseContentIds,
    destUserInfo,
    error,
    refresh,
  };
};

